import { BRAND_BACKDROP_URI } from "@/design/brandBackdrop";
import { useState } from "react";
import { StyleSheet, View } from "react-native";

import type { Recital } from "@recital-master/shared";
import {
  countWatchedDancesByPerformance,
  formatDuration,
  formatDurationLong
} from "@recital-master/shared";

import { AppHeader } from "@/components/AppHeader";
import { MediaCard } from "@/components/MediaCard";
import { PinkChip } from "@/components/PinkChip";
import { useAccount } from "@/data/AccountProvider";
import {
  useCatalog,
  useDances,
  usePerformanceDetailsStatus,
  usePerformances,
  useRecitalArtwork,
  useRecitals
} from "@/data/CatalogProvider";
import { GearIcon } from "@/design/icons";
import { Screen } from "@/design/primitives";
import { Muted, Overline, Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";
import { UtilityChip } from "@/design/UtilityChip";
import { displayTitle, joinWithDot } from "@/lib/format";
import { useAppRouter } from "@/lib/appRouter";
import { dancersPath, recitalPath } from "@/lib/routes";
import { recitalMyDancerWatchPath } from "@/playback/watchRoute";
import { useReadinessHold } from "@/providers/ReadinessGate";

const HOME_BACKDROP_URI = BRAND_BACKDROP_URI;

function RecitalCard({ recital }: { recital: Recital }) {
  const router = useAppRouter();
  const { catalog, preparePerformance, prepareRecitalOverview } = useCatalog();
  const performancesForRecital = usePerformances(recital.recitalId);
  const artwork = useRecitalArtwork(recital.recitalId);
  const studio = catalog?.studios.find(
    (entry) => entry.studioId === recital.studioId
  );
  const { resume } = useAccount();

  const danceCount = performancesForRecital.reduce((sum, performance) => sum + performance.danceCount, 0);
  const performances = performancesForRecital.length;
  const totalDuration = performancesForRecital.reduce(
    (sum, performance) => sum + performance.totalDurationSeconds,
    0
  );

  // Same watched math as the recital page — the card is the recital's face
  // on home and must tell the same story.
  const watchedDanceCount = countWatchedDancesByPerformance(
    performancesForRecital.map((performance) => ({
      danceIds: (catalog?.dances[performance.performanceId] ?? []).map((dance) => dance.danceId),
      watchedDanceIds: resume[performance.performanceId]?.watchedDanceIds,
      danceCount: performance.danceCount
    }))
  );
  const completionProgress =
    watchedDanceCount > 0 && danceCount > 0
      ? {
          fraction: watchedDanceCount / danceCount,
          label:
            watchedDanceCount === danceCount
              ? `All ${danceCount} watched`
              : `${watchedDanceCount} of ${danceCount} watched`
        }
      : undefined;

  // Most recent resume point across the recital's performances.
  const latestResume = performancesForRecital
    .map((performance) => resume[performance.performanceId])
    .filter((state) => state && !state.completed)
    .sort((a, b) => (a!.updatedAt < b!.updatedAt ? 1 : -1))[0];
  const resumeDances = useDances(latestResume?.performanceId);
  const resumeDetailsStatus = usePerformanceDetailsStatus(latestResume?.performanceId);
  const resumeDancesReady = !latestResume || resumeDetailsStatus !== "loading";
  useReadinessHold(
    latestResume ? `resume-${latestResume.performanceId}` : `resume-none-${recital.recitalId}`,
    resumeDancesReady
  );
  const resumePerformance = latestResume
    ? performancesForRecital.find((performance) => performance.performanceId === latestResume.performanceId)
    : undefined;
  const resumeDance = latestResume
    ? resumeDances.find((dance) => dance.danceId === latestResume.danceId) ?? null
    : null;

  const watchPrepared = (performanceId: string, path: string) => {
    void preparePerformance(performanceId)
      .catch(() => {})
      .then(() => router.push(path as never));
  };

  const playFullRecital = () => {
    void Promise.all(
      performancesForRecital.map((performance) =>
        preparePerformance(performance.performanceId).catch(() => {})
      )
    ).then(() =>
      router.push(`/watch/${recital.recitalId}?mode=full-recital` as never)
    );
  };

  const openPrepared = () => {
    void prepareRecitalOverview(recital.recitalId).then(() =>
      router.push(recitalPath(recital) as never)
    );
  };

  return (
    <MediaCard
      title={recital.title}
      kicker={joinWithDot([studio?.name, `${recital.year} Recital`])}
      metaLines={[
        joinWithDot([
          `${performances} ${performances === 1 ? "show" : "shows"}`,
          `${danceCount} dances`,
          formatDurationLong(totalDuration)
        ])
      ]}
      progress={completionProgress}
      artworkUri={artwork}
      backdropUri={HOME_BACKDROP_URI}
      inheritPageBackdrop
      artworkSeed={recital.recitalId}
      artworkLabel={String(recital.year)}
      accessibilityLabel={`Open ${recital.title}`}
      onPress={openPrepared}
      action={
        latestResume && resumePerformance
          ? {
              overline: resumeDance
                ? joinWithDot(["Resume", resumePerformance.title, `${formatDuration(Math.max(0, resumeDance.durationSeconds - latestResume.positionSeconds))} left`])
                : joinWithDot(["Resume", resumePerformance.title]),
              label: displayTitle(resumeDance?.title ?? "Continue watching"),
              onPress: () =>
                watchPrepared(
                  latestResume.performanceId,
                  `/watch/${latestResume.performanceId}?mode=${latestResume.mode}&dance=${latestResume.danceId}&at=${Math.floor(latestResume.positionSeconds)}`
                )
            }
          : {
              overline: "Start watching",
              label: "Play full recital",
              onPress: playFullRecital
            }
      }
    />
  );
}

/**
 * My Dancer entry point on home: saved dancers play with one tap; nothing
 * saved yet gets a warm setup card. Both states live in the same card so the
 * section doesn't change shape when dancers arrive. Playback spans every
 * matching routine in the saved dancer's recital.
 */
function MyDancersSection() {
  const router = useAppRouter();
  const { catalog, preparePerformance } = useCatalog();
  const { savedDancers } = useAccount();
  const recitals = useRecitals();
  const [sectionWidth, setSectionWidth] = useState(0);
  const narrow = sectionWidth > 0 && sectionWidth < 560;

  const newestRecital = [...recitals].sort((a, b) => b.year - a.year)[0];
  if (!catalog || !newestRecital) return null;

  const playDancer = (dancerId: string, recitalId: string) => {
    const recitalPerformances = catalog.performances.filter((performance) => performance.recitalId === recitalId);
    if (recitalPerformances.length > 0) {
      void Promise.all(
        recitalPerformances.map((performance) => preparePerformance(performance.performanceId).catch(() => {}))
      ).then(() =>
        router.push(recitalMyDancerWatchPath(recitalId, { dancerId }) as never)
      );
    } else {
      const recital = catalog.recitals.find((entry) => entry.recitalId === recitalId);
      if (recital) router.push(recitalPath(recital) as never);
    }
  };

  return (
    <View
      style={styles.dancersSection}
      onLayout={(event) => setSectionWidth(event.nativeEvent.layout.width)}
    >
      <View style={styles.dancersHeader}>
        <Overline>My dancers</Overline>
        <UtilityChip
          accessibilityLabel="Manage my dancers"
          icon={<GearIcon size={13} color={palette.muted} />}
          label="Manage"
          onPress={() => router.push("/account")}
        />
      </View>
      <View style={[styles.dancersCard, narrow && styles.dancersCardNarrow]}>
        {savedDancers.length === 0 ? (
          <>
            <View style={[styles.dancersSetupText, narrow && styles.setupTextNarrow]}>
              <Text variant="title">Find your dancer</Text>
              <Muted variant="small">
                Save them once and every recital gathers their dances
                automatically.
              </Muted>
            </View>
            <PinkChip
              label="Find my dancer"
              fullWidth={narrow}
              onPress={() => router.push(dancersPath(newestRecital) as never)}
            />
          </>
        ) : (
          <>
            <View style={[styles.dancersSetupText, narrow && styles.setupTextNarrow]}>
              <Text variant="title">
                {savedDancers.length === 1
                  ? `${savedDancers[0].name.split(" ")[0]}’s dances`
                  : "Your dancer playlists"}
              </Text>
              <Muted variant="small">
                {savedDancers.length === 1
                  ? `Every recital routine featuring ${savedDancers[0].name.split(" ")[0]}, ready to play back-to-back.`
                  : "Choose a dancer to play all of their recital routines back-to-back."}
              </Muted>
            </View>
            <View style={[styles.dancersRow, narrow && styles.dancersColumn]}>
              {savedDancers.map((dancer) => (
                <PinkChip
                  key={dancer.dancerId}
                  label={`Play ${dancer.name.split(" ")[0]}’s dances`}
                  fullWidth={narrow}
                  onPress={() => playDancer(dancer.dancerId, dancer.recitalId)}
                />
              ))}
            </View>
          </>
        )}
      </View>
    </View>
  );
}

export default function RecitalsHomeScreen() {
  const recitals = useRecitals();
  const { status, catalog } = useCatalog();
  const { accessExpiresAt } = useAccount();
  const accessExpiryMs = accessExpiresAt ? Date.parse(accessExpiresAt) : Number.NaN;
  const accessEnded = Number.isFinite(accessExpiryMs) && accessExpiryMs <= Date.now();

  // Newest first — the same order the list renders in. Home keeps a stable
  // Recital Master identity while each recital card retains its own artwork.
  const sortedRecitals = [...recitals].sort((a, b) => b.year - a.year);

  return (
    <Screen header={<AppHeader />} backdropUri={HOME_BACKDROP_URI}>
      {status === "error" ? (
        <View style={styles.hero}>
          <Text variant="title">We couldn’t load your recitals.</Text>
          <Muted>Check your connection and refresh — your access is safe.</Muted>
        </View>
      ) : null}
      {status === "ready" && catalog && recitals.length === 0 ? (
        <View style={styles.hero}>
          <Text variant="title">
            {accessEnded ? "Your recital access has ended." : "No recitals here yet."}
          </Text>
          <Muted>
            {accessEnded
              ? "Your account is safe. Contact Recital Master if you need help with access."
              : "When your studio publishes your recital, it will appear right here."}
          </Muted>
        </View>
      ) : null}
      {status === "ready" && catalog ? (
        <>
          <View style={styles.hero}>
            <Overline>Your library</Overline>
            <Text variant="hero" style={styles.heroTitle}>
              Your recitals
            </Text>
            <Muted>Press play on any show, any dance, any dancer.</Muted>
          </View>
          <View style={styles.list}>
            {sortedRecitals.map((recital) => (
              <RecitalCard key={recital.recitalId} recital={recital} />
            ))}
          </View>
          <MyDancersSection />
        </>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  hero: {
    gap: space.sm,
    marginBottom: space.xxl,
    marginTop: 0
  },
  heroTitle: {
    marginTop: space.xs
  },
  list: {
    gap: space.xl
  },
  dancersSection: {
    gap: space.md,
    marginTop: space.xxl
  },
  dancersHeader: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  dancersCard: {
    alignItems: "center",
    backgroundColor: palette.surface,
    borderColor: palette.hairline,
    borderRadius: radius.lg,
    borderWidth: 1,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.lg,
    justifyContent: "space-between",
    padding: space.xl
  },
  dancersCardNarrow: {
    alignItems: "stretch",
    flexDirection: "column",
    flexWrap: "nowrap",
    padding: space.lg
  },
  dancersSetupText: {
    flex: 1,
    gap: space.sm,
    minWidth: 220
  },
  setupTextNarrow: {
    flex: 0,
    minWidth: 0
  },
  dancersRow: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.sm,
    justifyContent: "flex-end"
  },
  dancersColumn: {
    alignItems: "stretch",
    flexDirection: "column",
    flexWrap: "nowrap"
  }
});
